import { useEffect, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatPhoneForDisplay } from "@/lib/format-phone";
import { startLeadConversation } from "@/lib/start-lead-conversation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

type PhoneRow = { phone: string; is_primary: boolean };
type Sibling = { id: string; external_user_id: string | null; unread_count: number | null; is_closed: boolean | null };

interface ConversationPhoneSwitchProps {
  workspaceId: string;
  leadId: string;
  leadName: string;
  integrationId: string | null;
  channel: string;
  currentConversationId: string;
  currentNumber: string | null;
  onSwitched: (conversation: {
    id: string;
    external_user_id: string | null;
    state?: string;
    followup_enabled?: boolean;
    is_closed?: boolean;
    integration_id?: string | null;
    channel: string;
  }) => void;
}

function digits(value: string | null | undefined): string {
  return (value ?? "").replace(/\D/g, "");
}

function sameNumber(a: string | null | undefined, b: string | null | undefined): boolean {
  const da = digits(a);
  const db = digits(b);
  if (!da || !db) return false;
  return da === db || da.endsWith(db) || db.endsWith(da);
}

export function ConversationPhoneSwitch({
  workspaceId,
  leadId,
  leadName,
  integrationId,
  channel,
  currentConversationId,
  currentNumber,
  onSwitched,
}: ConversationPhoneSwitchProps) {
  const [phones, setPhones] = useState<PhoneRow[]>([]);
  const [siblings, setSiblings] = useState<Sibling[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [{ data: phoneRows }, { data: convRows }] = await Promise.all([
        supabase
          .from("lead_phones")
          .select("phone, is_primary")
          .eq("lead_id", leadId)
          .eq("workspace_id", workspaceId)
          .order("is_primary", { ascending: false }),
        supabase
          .from("conversations")
          .select("id, external_user_id, unread_count, is_closed, channel, integration_id")
          .eq("lead_id", leadId)
          .eq("workspace_id", workspaceId)
          .eq("channel", channel),
      ]);
      if (cancelled) return;
      setPhones((phoneRows as PhoneRow[]) ?? []);
      const sameIntegration = ((convRows ?? []) as (Sibling & { integration_id?: string | null; channel?: string })[])
        .filter((row) => (row.integration_id ?? null) === (integrationId ?? null));
      setSiblings(sameIntegration);
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, workspaceId, channel, integrationId, currentConversationId]);

  const shownNumber =
    siblings.find((row) => row.id === currentConversationId)?.external_user_id || currentNumber;

  const otherUnread = siblings.some(
    (row) => row.id !== currentConversationId && !row.is_closed && (row.unread_count ?? 0) > 0,
  );

  const openPhone = async (phone: string) => {
    const existing = siblings.find((row) => sameNumber(row.external_user_id, phone));
    if (existing && existing.id !== currentConversationId) {
      onSwitched({
        id: existing.id,
        external_user_id: existing.external_user_id,
        channel,
        integration_id: integrationId,
        is_closed: false,
      });
      return;
    }
    if (existing?.id === currentConversationId) return;
    if (channel !== "whatsapp") return;
    setBusy(true);
    try {
      const result = await startLeadConversation({
        workspaceId,
        leadId,
        channel: "whatsapp",
        integrationId,
        phone,
      });
      if (result.ok && result.conversation) {
        onSwitched({
          ...result.conversation,
          external_user_id: phone,
          channel: result.conversation.channel,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const label = formatPhoneForDisplay(shownNumber) || shownNumber || "";
  const choices = phones.filter((row) => !sameNumber(row.phone, shownNumber));
  if (choices.length === 0) {
    return label ? <span className="truncate tabular-nums">{label}</span> : null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "inline-flex max-w-full items-center gap-1 truncate tabular-nums",
          otherUnread && "animate-pulse font-medium text-amber-600 dark:text-amber-400",
        )}
        disabled={busy}
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
        <span className="truncate">{label}</span>
        {otherUnread ? <span className="shrink-0">Mensagem no outro número</span> : null}
        <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {choices.map((row) => {
          const sibling = siblings.find((s) => !s.is_closed && sameNumber(s.external_user_id, row.phone));
          const unread = (sibling?.unread_count ?? 0) > 0;
          return (
            <DropdownMenuItem key={row.phone} onClick={() => void openPhone(row.phone)}>
              <span className="tabular-nums">{formatPhoneForDisplay(row.phone) || row.phone}</span>
              {row.is_primary ? <span className="ml-2 text-xs text-muted-foreground">principal</span> : null}
              {unread ? <span className="ml-2 text-xs text-amber-600">mensagem</span> : null}
            </DropdownMenuItem>
          );
        })}
        <p className="px-2 py-1.5 text-[11px] text-muted-foreground">Conversando com {leadName}</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
